import { useTranslation } from 'react-i18next';

import { BetSummaryProps, BetSummarySectionProps } from '@/components/BetSummarySection';
import { ds } from '@/components/lib/design.system';
import { Bet } from '@/feature/bets-sbk/hooks/types';
import { getAmountWonWithBonus, getNoSweatRefund } from '@/feature/bets-sbk/utils/bet-promo-utils';
import { useFormatCurrency } from '@/hooks/use-format-currency';

import { useSharedBetSummaryProps, useVoidedBetSummary } from '../bet-cards/utils';

/**
 * Shared bet summary props for Bet Details
 */
export const useSharedBetDetailsBetSummary = (props: BetSummaryProps) => {
    const bodyTextVariant = 'titleMedium' as const;
    return {
        ...useSharedBetSummaryProps(props),
        bodyTextVariant,
    };
};

type SettledPayoutSummary = Pick<
    BetSummarySectionProps,
    'header3Text' | 'body3Text' | 'header3TextColor' | 'body3TextColor' | 'body3Icon'
>;

/**
 * Payout cell for a settled bet, shared by the single and multi summaries so both agree with the
 * entry card. A rewarded No Sweat refund reads as "Bonus payout" in Betr Bucks; every other outcome
 * shows the cash "Paid" amount (Profit Boost bonus included), green only when positive.
 */
export const useSettledPayoutSummary = (bet: Bet): SettledPayoutSummary => {
    const { t } = useTranslation('bets');
    const { formatCurrency } = useFormatCurrency();

    const refund = getNoSweatRefund(bet);
    if (refund != null) {
        return {
            header3Text: t('bonus_payout'),
            body3Text: formatCurrency(refund),
            header3TextColor: ds.colorToken.text.secondary,
            body3TextColor: ds.colorToken.text.primary,
            body3Icon: 'bonus',
        };
    }

    const amountWon = getAmountWonWithBonus(bet);
    return {
        header3Text: t('paid'),
        body3Text: formatCurrency(amountWon),
        header3TextColor: amountWon > 0 ? ds.colorToken.text.success : ds.colorToken.text.secondary,
        body3TextColor: amountWon > 0 ? ds.colorToken.text.success : ds.colorToken.text.secondary,
    };
};

/**
 * Handles all 'Voided' bet summary for Bet Details
 */
export const useBetDetailsVoidedBetSummary = (props: BetSummaryProps): BetSummarySectionProps => {
    const { bet } = props;
    return {
        ...useSharedBetDetailsBetSummary(props),
        ...useVoidedBetSummary(bet, 'neutral400'),
    };
};
